import { iterate } from './util.js';

const ORIGINAL_STYLE_SYMBOL = Symbol('vx:originalStyle');

export const INTERACTIVE_TAGS = ['a', 'button', 'input', 'textarea', 'select', 'label', 'option', 'optgroup'];
export const INTERACTIVE_ROLES = [
    'clickable', 'button', 'link', 'checkbox', 'radio', 'textbox', 'combobox', 'listbox', 'menu', 'menuitem', 'menuitemcheckbox', 'menuitemradio', 'option', 'optgroup', 'progressbar', 'scrollbar', 'slider', 'spinbutton', 'switch', 'tab', 'tablist', 'timer', 'toolbar'];

export interface Viewport {
    width: number;
    height: number;
}

export function isHidden(el: Element) {
    const style = getComputedStyle(el);
    if (style.display === 'none') {
        return true;
    }
    if (style.visibility === 'hidden') {
        return true;
    }
    if (style.opacity === '0') {
        return true;
    }
    return false;
}

export function getNormalizedText(el: Element): string {
    return normalizeText(el.textContent ?? '');
}

export function normalizeText(str: string) {
    return str
        .replace(/\p{Cf}/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export function truncateAttrValue(value: string, limit = 100) {
    if (value.match(/^(javascript:)?void/)) {
        return '';
    }
    if (value.startsWith('data:')) {
        return '';
    }
    if (value.length > limit) {
        return value.slice(0, limit) + '…';
    }
    return value;
}

export function escapeAttribute(value: string) {
    return value
        .replace(/&/g, '&amp;') // Escape ampersand first!
        .replace(/"/g, '&quot;') // Escape double quotes
        .replace(/'/g, '&#39;') // Escape single quotes
        .replace(/</g, '&lt;') // Escape less than
        .replace(/>/g, '&gt;') // Escape greater than
        .replace(/\r?\n/g, ' '); // Replace newlines with space
}

export function containsSelector(el: Element, selector: string): boolean {
    return el.matches(selector) || !!el.querySelector(selector);
}

export function getOffsetTop(node: Node) {
    let y = 0;
    let current: any = node;
    while (current) {
        y += current.offsetTop ?? 0;
        current = current.offsetParent;
    }
    return y;
}

export function hasVisibleArea(element: Element) {
    if (!element) {
        return false;
    }
    const rect = element.getBoundingClientRect();
    const area = rect.width * rect.height;
    return area > 64;
}

export function isDeepHidden(element: Element): boolean {
    if (isHidden(element)) {
        return true;
    }
    if (!hasVisibleArea(element)) {
        if (element.children.length === 0) {
            return true;
        }
        for (const child of iterate(element.children)) {
            if (!isDeepHidden(child)) {
                return false;
            }
        }
        return true;
    }
    return false;
}

export function isInteractive(el: Element) {
    const htmlEl = el as HTMLElement;
    if (INTERACTIVE_TAGS.includes(el.tagName.toLowerCase())) {
        return true;
    }
    const tabindex = htmlEl.getAttribute('tabindex');
    if (tabindex && parseInt(tabindex) >= 0) {
        return true;
    }
    const role = htmlEl.getAttribute('role') ?? htmlEl.getAttribute('aria-role') ?? '';
    if (INTERACTIVE_ROLES.includes(role)) {
        return true;
    }
    if (htmlEl.onclick != null) {
        return true;
    }
    const styles = getComputedStyle(htmlEl);
    if (styles.cursor === 'pointer') {
        return true;
    }
    return false;
}

export function getViewportSize(): Viewport {
    return {
        width: window.innerWidth,
        height: window.innerHeight,
    };
}

export function isRectInViewport(rect: DOMRect, viewport: Viewport) {
    return rect.left < viewport.width &&
        rect.right > 0 &&
        rect.top < viewport.height &&
        rect.bottom > 0;
}

export function makeOverlaysOpaque(el: HTMLElement) {
    if (!el) {
        return;
    }
    if (!(el as any)[ORIGINAL_STYLE_SYMBOL]) {
        (el as any)[ORIGINAL_STYLE_SYMBOL] = el.getAttribute('style');
    }
    // Only apply to big elements
    const rect = el.getBoundingClientRect();
    if (rect.width < 500 || rect.height < 500) {
        return;
    }
    const style = getComputedStyle(el);
    if (style.pointerEvents === 'none') {
        return;
    }
    el.style.setProperty('animation', 'none', 'important');
    el.style.setProperty('transition', 'none', 'important');
    el.style.setProperty('backdrop-filter', 'none', 'important');
    el.style.setProperty('mix-blend-mode', 'normal', 'important');
    el.style.setProperty('opacity', '1', 'important');
    el.style.setProperty('filter', 'none', 'important');
    const bg = style.backgroundColor;
    el.style.setProperty('background-color', removeAlpha(bg));
}

function removeAlpha(color: string) {
    const rgba = color.match(/^rgba\((.*)\)$/);
    if (!rgba) {
        return color;
    }
    const [r, g, b, a] = rgba[1].split(',').map(parseFloat);
    if (a > 0 && a < 1) {
        return `rgba(${r},${g},${b},1)`;
    }
    return color;
}

export function isRandomIdentifier(str: string) {
    const words = str.split(/[_-]/g);
    return !words.some(w => isWordLike(w)) || words.some(w => isRandomString(w));
}

function isWordLike(str: string) {
    if (str.length <= 2) {
        return false;
    }
    // Words don't usually have a lot of vowels or consonants in a row
    if (/[^eyuioa]{4,}/.test(str)) {
        return false;
    }
    if (/[eyuioa]{4,}/.test(str)) {
        return false;
    }
    return true;
}

function isRandomString(str: string) {
    // Hex strings will often consist of at least one digit and A-F letters
    if (/^[0-9a-f]+$/i.test(str) && /[0-9]/.test(str)) {
        return true;
    }
    // Digits separated by non-digits are a sign of random ids
    if (/[0-9][^0-9]+[0-9]/.test(str)) {
        return true;
    }
    return false;
}

export function fixZIndex(el: HTMLElement) {
    const _style = getComputedStyle(el);
    const zIndex = parseInt(_style.zIndex);
    if (!isNaN(zIndex)) {
        const maxZIndex = 2147483647;
        if (zIndex > maxZIndex - 200) {
            el.style.zIndex = String(zIndex - 1);
        }
    }
}

export function getElementDepth(el: Element) {
    let depth = 0;
    while (el.parentElement) {
        el = el.parentElement;
        depth += 1;
    }
    return depth;
}
